/** Toast — Radix Toast wired into a global toaster with NVL styling. */
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { CheckCircle2, Info, AlertTriangle, XCircle, X } from "lucide-react";
import { cn } from "../../lib/cn";

const ToastContext = createContext(null);

const variantStyles = {
  success: { Icon: CheckCircle2, cls: "border-sage/40 bg-bg-elev", iconCls: "text-sage" },
  info: { Icon: Info, cls: "border-mauve/40 bg-bg-elev", iconCls: "text-mauve" },
  warning: { Icon: AlertTriangle, cls: "border-gold/40 bg-bg-elev", iconCls: "text-gold" },
  error: { Icon: XCircle, cls: "border-red-500/40 bg-bg-elev", iconCls: "text-red-400" },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const toast = useCallback((opts) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, variant: "info", duration: 4500, ...opts }]);
    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map(({ id, variant, title, description, duration }) => {
          const { Icon, cls, iconCls } = variantStyles[variant] || variantStyles.info;
          return (
            <ToastPrimitive.Root
              key={id}
              duration={duration}
              onOpenChange={(open) => !open && dismiss(id)}
              className={cn(
                "border rounded-[18px] shadow-float p-4 pr-3 grid grid-cols-[auto_1fr_auto] items-start gap-3",
                "data-[state=open]:animate-reveal-up data-[state=closed]:opacity-0 transition-all",
                cls
              )}
              data-testid={`toast-${variant}`}
            >
              <Icon size={18} className={cn("mt-0.5", iconCls)} />
              <div className="min-w-0">
                {title && (
                  <ToastPrimitive.Title className="font-semibold text-[14px] text-text">
                    {title}
                  </ToastPrimitive.Title>
                )}
                {description && (
                  <ToastPrimitive.Description className="text-[13px] text-text-dim mt-0.5 leading-relaxed">
                    {description}
                  </ToastPrimitive.Description>
                )}
              </div>
              <ToastPrimitive.Close
                className="text-text-faint hover:text-text rounded-full w-8 h-8 inline-flex items-center justify-center hover:bg-surface-2 transition-colors"
                aria-label="Close"
              >
                <X size={14} />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport className="fixed bottom-6 right-6 z-[300] flex flex-col gap-2 w-[360px] max-w-[calc(100vw-3rem)] outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
